// components/tracking/StatusBadge.jsx - Enhanced version without redundant RTO badge
import React from 'react';

const StatusBadge = ({ status, statusCode, className = "", size = "default", hideRTOBadge = false }) => {
  const getStatusInfo = (status, code) => {
    const statusLower = status.toLowerCase();
    const codeStr = code ? code.toString() : '';

    // Success states
    if (codeStr === '100' || statusLower.includes('delivered')) {
      return { 
        color: 'bg-green-100 text-green-800 border-green-200', 
        dotColor: 'bg-green-500', 
        icon: '✓', 
        isRTO: false 
      };
    }
    if (codeStr === '160' || statusLower.includes('rto delivered')) {
      return { 
        color: 'bg-green-100 text-green-800 border-green-200', 
        dotColor: 'bg-green-500', 
        icon: '✓', 
        isRTO: true 
      };
    }
    if (codeStr === '280' || statusLower.includes('exchange successful')) {
      return { 
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
        dotColor: 'bg-emerald-500', 
        icon: '⇄', 
        isRTO: false 
      };
    }

    // In-progress states
    if (codeStr === '70' || statusLower.includes('in transit')) {
      return { 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        dotColor: 'bg-blue-500', 
        icon: '→', 
        isRTO: false 
      };
    }
    if (codeStr === '230' || statusLower.includes('rto in transit')) {
      return { 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        dotColor: 'bg-blue-500', 
        icon: '→', 
        isRTO: true 
      };
    }
    if (codeStr === '80' || statusLower.includes('out for delivery')) {
      return { 
        color: 'bg-purple-100 text-purple-800 border-purple-200', 
        dotColor: 'bg-purple-500', 
        icon: '🚚', 
        isRTO: false 
      };
    }
    if (codeStr === '150' || statusLower.includes('rto out for delivery')) {
      return { 
        color: 'bg-purple-100 text-purple-800 border-purple-200', 
        dotColor: 'bg-purple-500', 
        icon: '🚚', 
        isRTO: true 
      };
    }

    // Pickup states
    if (codeStr === '60' || statusLower.includes('pickup has been done') || statusLower.includes('picked up')) {
      return { 
        color: 'bg-orange-100 text-orange-800 border-orange-200', 
        dotColor: 'bg-orange-500', 
        icon: '📦', 
        isRTO: false 
      };
    }
    if (codeStr === '40' || statusLower.includes('pickup pending')) {
      return { 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        dotColor: 'bg-yellow-500', 
        icon: '⏳', 
        isRTO: false 
      };
    }

    // RTO states
    if (codeStr === '130' || statusLower.includes('rto requested')) {
      return { 
        color: 'bg-orange-100 text-orange-800 border-orange-200', 
        dotColor: 'bg-orange-500', 
        icon: '↩', 
        isRTO: true 
      };
    }
    if (codeStr === '140' || statusLower.includes('marked as return')) {
      return { 
        color: 'bg-orange-100 text-orange-800 border-orange-200', 
        dotColor: 'bg-orange-500', 
        icon: '↩', 
        isRTO: true 
      };
    }
    if (codeStr === '170' || statusLower.includes('rto failed')) {
      return { 
        color: 'bg-red-100 text-red-800 border-red-200', 
        dotColor: 'bg-red-500', 
        icon: '✕', 
        isRTO: true 
      };
    }

    // Failed/Error states
    if (codeStr === '50' || statusLower.includes('pickup failed')) {
      return { 
        color: 'bg-red-100 text-red-800 border-red-200', 
        dotColor: 'bg-red-500', 
        icon: '✕', 
        isRTO: false 
      };
    }
    if (codeStr === '110' || statusLower.includes('delivery failed')) {
      return { 
        color: 'bg-red-100 text-red-800 border-red-200', 
        dotColor: 'bg-red-500', 
        icon: '✕', 
        isRTO: false 
      };
    }
    if (codeStr === '120' || statusLower.includes('cancelled')) {
      return { 
        color: 'bg-gray-100 text-gray-800 border-gray-200', 
        dotColor: 'bg-gray-500', 
        icon: '⊘', 
        isRTO: false 
      };
    }

    // Default for processing/pending
    const isRTOStatus = statusLower.includes('rto');
    return { 
      color: 'bg-slate-100 text-slate-800 border-slate-200', 
      dotColor: 'bg-slate-500', 
      icon: '○', 
      isRTO: isRTOStatus 
    };
  };

  const statusInfo = getStatusInfo(status, statusCode);
  
  const sizeClasses = {
    small: 'px-3 py-1.5 gap-2 text-xs',
    default: 'px-3 py-2 gap-2 text-sm',
    large: 'px-4 py-2.5 gap-3 text-base'
  };

  const dotSizes = {
    small: 'w-1.5 h-1.5',
    default: 'w-2 h-2',
    large: 'w-2.5 h-2.5'
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Main Status Badge */}
      <div className={`
        inline-flex items-center rounded-full border
        shadow-sm transition-all duration-200 hover:shadow-md
        ${statusInfo.color} ${sizeClasses[size]} ${className}
      `}>
        {/* Status Dot */}
        <div className={`
          ${dotSizes[size]} rounded-full ${statusInfo.dotColor} 
          shadow-sm
        `}></div>
        
        {/* Status Text */}
        <span className="font-semibold leading-tight flex-1">
          {status}
        </span>
        
        {/* Status Icon */}
        <span className="font-medium opacity-80">
          {statusInfo.icon}
        </span>
      </div>
      
    </div>
  );
};

export default StatusBadge;